import axios from 'axios';
import { apiPath, withBasePath } from '../utils/appPaths';

const API_BASE_URL = apiPath();
export const ADMIN_AUTH_EXPIRED_EVENT = 'admin-auth-expired';

const adminApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const clearStoredAdminCredentials = () => {
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminUser');
};

export const handleAdminSessionExpired = () => {
  clearStoredAdminCredentials();

  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new Event(ADMIN_AUTH_EXPIRED_EVENT));

  const loginPath = withBasePath('/admin');
  const currentPath = window.location.pathname;
  const normalizedLoginPath = loginPath.replace(/\/+$/, '') || '/';
  const normalizedCurrentPath = currentPath.replace(/\/+$/, '') || '/';

  if (normalizedCurrentPath !== normalizedLoginPath) {
    window.location.replace(loginPath);
  }
};

// Add token to requests
adminApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = Number(error?.response?.status);
    const requestUrl = String(error?.config?.url || '');
    const isLoginRequest = requestUrl.includes('/admin/login');

    if (status === 401 && !isLoginRequest) {
      handleAdminSessionExpired();
    }

    return Promise.reject(error);
  }
);

export const adminFetch = async (path, init = {}) => {
  const token = localStorage.getItem('adminToken');
  const headers = new Headers(init.headers || {});

  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(apiPath(path), {
    ...init,
    headers,
  });

  if (response.status === 401) {
    handleAdminSessionExpired();
  }

  return response;
};

// Admin Authentication
export const adminAuth = {
  login: (username, password) => 
    adminApi.post('/admin/login', { username, password }),
  
  logout: () => {
    clearStoredAdminCredentials();
  }
};

// Admin Boards API
export const adminBoardsAPI = {
  getAll: () => adminApi.get('/admin/boards'),
  getById: (id) => adminApi.get(`/admin/boards/${id}`),
  getByCategory: (category) => adminApi.get(`/admin/wiring-guide/boards?category=${category}`),
  create: (boardData) => adminApi.post('/admin/boards', boardData),
  update: (id, boardData) => adminApi.put(`/admin/boards/${id}`, boardData),
  uploadPublicPinoutPng: (id, formData) =>
    adminApi.post(`/admin/boards/${id}/public-pinout-png`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  updateProjectBuilder: (id, data) => adminApi.put(`/admin/boards/${id}/project-builder`, data),
  rebuildSvg: (id, data) => adminApi.post(`/admin/boards/${id}/rebuild-svg`, data),
  delete: (id) => adminApi.delete(`/admin/boards/${id}`),
  publish: (id, published) => adminApi.put(`/admin/boards/${id}/publish`, { published }),
  togglePopularOnHome: (id, popular_on_home) => adminApi.put(`/admin/boards/${id}/popular`, { popular_on_home }),
  getPins: (id) => adminApi.get(`/admin/boards/${id}/pins`),
  createFromFritzing: (boardData, fritzingData) => 
    adminApi.post('/admin/boards/from-fritzing', { boardData, fritzingData })
};

// Admin Pins API
export const adminPinsAPI = {
  update: (id, pinData) => adminApi.put(`/admin/pins/${id}`, pinData),
  bulkUpdate: (pins) => adminApi.post('/admin/pins/bulk-update', { pins }),
  delete: (id) => adminApi.delete(`/admin/pins/${id}`)
};

// Admin Wiring Guides API (plural - for managing existing guides)
export const adminWiringGuidesAPI = {
  getAll: () => adminApi.get('/admin/wiring-guides'),
  getById: (id) => adminApi.get(`/admin/wiring-guide/${id}`),
  publish: (id, published) => adminApi.put(`/admin/wiring-guide/${id}/publish`, { published }),
  update: (id, data) => adminApi.put(`/admin/wiring-guide/${id}`, data),
  delete: (id) => adminApi.delete(`/admin/wiring-guide/${id}`)
};

export const adminGuidesAPI = {
  getAll: () => adminApi.get('/admin/guides'),
  getById: (id) => adminApi.get(`/admin/guides/${id}`),
  create: (data) => adminApi.post('/admin/guides', data),
  update: (id, data) => adminApi.put(`/admin/guides/${id}`, data),
  delete: (id) => adminApi.delete(`/admin/guides/${id}`),
  uploadImage: (formData) =>
    adminApi.post('/admin/guides/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
};

// Admin Pin Groups API
export const adminPinGroupsAPI = {
  getAll: () => adminApi.get('/admin/pin-groups'),
  create: (groupData) => adminApi.post('/admin/pin-groups', groupData)
};

// Admin Categories API
export const adminCategoriesAPI = {
  getAll: () => adminApi.get('/admin/categories')
};

// Admin Upload API
export const adminUploadAPI = {
  uploadFritzing: (formData) => 
    adminApi.post('/admin/upload-fritzing', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
  testFritzingData: () => adminApi.post('/admin/test-fritzing-data')
};

// Admin Wiring Guide API (singular - for generating new guides)
export const adminWiringGuideAPI = {
  preview: (data) => adminApi.post('/admin/wiring-guide/preview', data),
  generate: (data) => adminApi.post('/admin/wiring-guide/generate', data)
};

export const adminIcPinoutsAPI = {
  create: (data) => adminApi.post('/admin/ic-pinouts', data),
  getAll: () => adminApi.get('/admin/ic-pinouts'),
  getById: (id) => adminApi.get(`/admin/ic-pinouts/${id}`),
  update: (id, data) => adminApi.put(`/admin/ic-pinouts/${id}`, data),
  delete: (id) => adminApi.delete(`/admin/ic-pinouts/${id}`),
  publish: (id, published) => adminApi.put(`/admin/ic-pinouts/${id}/publish`, { published })
};

export const adminPassiveComponentsAPI = {
  getAll: () => adminApi.get('/admin/passive-components'),
  create: (data) => adminApi.post('/admin/passive-components', data),
  update: (id, data) => adminApi.put(`/admin/passive-components/${id}`, data),
  delete: (id) => adminApi.delete(`/admin/passive-components/${id}`),
};

export const adminProjectBuilderAPI = {
  getSettings: () => adminApi.get('/admin/project-builder/settings'),
  updateSettings: (data) => adminApi.put('/admin/project-builder/settings', data),
  getExamples: () => adminApi.get('/admin/project-builder/examples'),
  createExample: (data) => adminApi.post('/admin/project-builder/examples', data),
  updateExample: (id, data) => adminApi.put(`/admin/project-builder/examples/${id}`, data),
  deleteExample: (id) => adminApi.delete(`/admin/project-builder/examples/${id}`),
  getLessons: () => adminApi.get('/admin/project-builder/lessons'),
  getLesson: (id) => adminApi.get(`/admin/project-builder/lessons/${id}`),
  createLesson: (data) => adminApi.post('/admin/project-builder/lessons', data),
  updateLesson: (id, data) => adminApi.put(`/admin/project-builder/lessons/${id}`, data),
  deleteLesson: (id) => adminApi.delete(`/admin/project-builder/lessons/${id}`),
};

export const adminAdsAPI = {
  getAll: () => adminApi.get('/admin/ads'),
  getBySlot: (slotKey) => adminApi.get(`/admin/ads/${slotKey}`),
  update: (slotKey, data) => adminApi.put(`/admin/ads/${slotKey}`, data),
  uploadImage: (slotKey, formData) =>
    adminApi.post(`/admin/ads/${slotKey}/upload-image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),
};

export default adminApi;
